"use client"

import { useState, useEffect } from "react"
import { AI_PROVIDERS, getAskAIContent, type PageContext } from "@/data/explain-with-ai"
import { cn } from "@/lib/utils"

type AskAIExplainProps = {
  /** The prompt to send to each AI provider (overrides auto-generated prompt from context) */
  prompt?: string
  /** Page context — drives title, description, and prompt automatically */
  context?: PageContext
  /** Meta info for auto-generating title/description/prompt */
  meta?: { competitorName?: string; articleTitle?: string; pageTitle?: string; pageUrl?: string }
  /** Title displayed above the AI buttons (overrides auto-generated) */
  title?: string
  /** Description shown under the title (overrides auto-generated) */
  description?: string
  /** Render as an inline card */
  isCard?: boolean
  /** Render as a floating bottom widget (appears on scroll) */
  isWidget?: boolean
  /** Additional className */
  className?: string
}

/**
 * AskAIExplain — reusable component for "Ask AI to explain" / "Summarize with AI"
 *
 * Two modes:
 * - isCard: renders inline as a bordered card section
 * - isWidget: renders as a floating single-row bottom widget that appears on scroll
 *
 * Both can be true simultaneously.
 */
export function AskAIExplain({
  prompt: promptProp,
  context = "generic",
  meta,
  title: titleProp,
  description: descProp,
  isCard = false,
  isWidget = false,
  className,
}: AskAIExplainProps) {
  const [showWidget, setShowWidget] = useState(false)
  const [widgetDismissed, setWidgetDismissed] = useState(false)

  // Auto-generate content based on context
  const autoContent = getAskAIContent(context, meta)
  const title = titleProp ?? autoContent.title
  const description = descProp ?? autoContent.description
  const prompt = promptProp ?? autoContent.prompt

  useEffect(() => {
    if (!isWidget) return
    const handleScroll = () => {
      if (widgetDismissed) return
      setShowWidget(window.scrollY > 600)
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [isWidget, widgetDismissed])

  const aiButtons = (
    <div className="flex flex-wrap items-center gap-2">
      {AI_PROVIDERS.map((provider) => (
        <a
          key={provider.id}
          href={provider.buildUrl(prompt)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 transition-all hover:border-neutral-300 hover:bg-neutral-50 hover:shadow-sm dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:border-neutral-600 dark:hover:bg-neutral-800"
        >
          <img
            src={provider.icon}
            alt={provider.name}
            width={16}
            height={16}
            className="size-5 rounded-full object-cover"
            loading="lazy"
          />
          <span>{provider.name}</span>
        </a>
      ))}
    </div>
  )

  return (
    <>
      {/* Card Mode */}
      {isCard && (
        <div
          className={cn(
            "rounded-2xl border border-neutral-200 bg-brand-accent/[0.03] p-5 dark:border-neutral-800 dark:bg-brand-accent/[0.03] sm:p-6",
            className
          )}
        >
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <p className="text-sm font-bold text-neutral-900 dark:text-white">{title}</p>
              <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{description}</p>
            </div>
          </div>
          {aiButtons}
        </div>
      )}

      {/* Widget Mode — floating bottom, compact single row with icons only */}
      {isWidget && showWidget && !widgetDismissed && (
        <div className="fixed left-1/2 z-50 -translate-x-1/2 animate-in slide-in-from-bottom-4 fade-in duration-300 bottom-[52px] sm:bottom-4">
          <div className="flex items-center gap-2.5 rounded-full border border-neutral-200 bg-white/95 px-4 py-2.5 shadow-lg backdrop-blur-md dark:border-neutral-700 dark:bg-neutral-900/95">
            {/* Label */}
            <span className="shrink-0 text-xs font-bold text-neutral-700 dark:text-neutral-200 whitespace-nowrap">
              Summarize with
            </span>

            {/* AI Icons only */}
            <div className="flex items-center gap-1.5">
              {AI_PROVIDERS.map((provider) => (
                <a
                  key={provider.id}
                  href={provider.buildUrl(prompt)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={provider.name}
                  className="inline-flex size-8 items-center justify-center rounded-full border border-neutral-200 bg-neutral-50 transition-all hover:border-neutral-300 hover:bg-white hover:shadow-sm dark:border-neutral-700 dark:bg-neutral-800 dark:hover:border-neutral-600 dark:hover:bg-neutral-700"
                >
                  <img
                    src={provider.icon}
                    alt={provider.name}
                    width={16}
                    height={16}
                    className="size-5 rounded-full object-cover"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>

            {/* Dismiss */}
            <button
              type="button"
              onClick={() => setWidgetDismissed(true)}
              className="shrink-0 rounded-full p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 dark:hover:bg-neutral-800 dark:hover:text-neutral-300"
              aria-label="Dismiss"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  )
}

/**
 * SummarizeWith — compact inline version for blog headers / cards
 * Shows "SUMMARIZE WITH" label + small AI icons in a row
 */
export function SummarizeWith({
  prompt,
  className,
}: {
  prompt: string
  className?: string
}) {
  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <span className="text-[11px] font-bold uppercase tracking-widest text-neutral-400 dark:text-neutral-500">
        Summarize with
      </span>
      {AI_PROVIDERS.map((provider) => (
        <a
          key={provider.id}
          href={provider.buildUrl(prompt)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-xs font-medium text-neutral-600 transition-all hover:border-neutral-300 hover:bg-white hover:shadow-sm dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:border-neutral-600 dark:hover:bg-neutral-700"
        >
          <img
            src={provider.icon}
            alt={provider.name}
            width={14}
            height={14}
            className="size-5 rounded-full object-cover"
            loading="lazy"
          />
          <span>{provider.name}</span>
        </a>
      ))}
    </div>
  )
}

export default AskAIExplain
